import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Collapse,
  Divider,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  Textarea,
  useDisclosure,
  VStack,
} from '@chakra-ui/react'
import { db, SolidActionButton, submitPatientStatement, useQuery } from '@hb/react-web'
import {
  Encounter,
  ENCOUNTERS,
  EncounterStatus,
  getPatientStatementsCollectionPath,
  PatientStatement,
  WithId,
} from '@hb/shared'
import { collection, CollectionReference, query, where } from 'firebase/firestore'
import { useCallback, useMemo, useState } from 'react'

interface Props {
  assessmentId: string
  patientId: string
  practiceId: string
}

const StatementModal = ({
  isOpen,
  onClose,
  encounter,
  patientId,
  assessmentId,
  practiceId,
}: {
  isOpen: boolean
  onClose: () => void
  encounter: WithId<Encounter>
  patientId: string
  assessmentId: string
  practiceId: string
}) => {
  const [text, setText] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const statementsRef = useMemo(
    () =>
      query(
        collection(
          db,
          getPatientStatementsCollectionPath(patientId),
        ) as CollectionReference<PatientStatement>,
        where('encounterId', '==', encounter.id),
      ),
    [encounter.id, patientId],
  )
  const { dataArr: existingStatements } = useQuery<PatientStatement>(statementsRef)

  const handleSubmit = useCallback(async () => {
    if (!text.trim()) return
    setSubmitting(true)
    setError(null)
    try {
      await submitPatientStatement({
        encounterId: encounter.id,
        encounterDate: encounter.date ?? '',
        patientId,
        assessmentId,
        practiceId,
        statementText: text.trim(),
      })
      setText('')
      onClose()
    } catch (err: any) {
      setError(err?.message ?? 'An error occurred. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }, [text, encounter, patientId, assessmentId, practiceId, onClose])

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="md" fontWeight={600}>
          Add a Written Statement
        </ModalHeader>
        <ModalBody>
          <Text fontSize="sm" color="gray.600" mb={3}>
            Visit date: {encounter.date ?? '—'}
          </Text>
          {(existingStatements ?? []).length > 0 ? (
            <Box mb={4}>
              {(existingStatements ?? []).map(s => (
                <Box
                  key={s.id}
                  border="1px solid"
                  borderColor="orange.200"
                  bg="orange.50"
                  borderRadius={6}
                  p={3}
                  mb={2}>
                  <Text fontSize="xs" color="gray.500" mb={1}>
                    You submitted a statement on {new Date(s.submittedAt).toLocaleString()}
                  </Text>
                  <Text fontSize="sm" whiteSpace="pre-wrap">
                    {s.statementText}
                  </Text>
                </Box>
              ))}
            </Box>
          ) : null}
          <Textarea
            value={text}
            onChange={e => setText(e.target.value)}
            placeholder="Describe your disagreement with this record entry..."
            rows={6}
            fontSize="sm"
          />
          {error ? (
            <Alert status="error" mt={2} fontSize="xs">
              <AlertIcon />
              {error}
            </Alert>
          ) : null}
        </ModalBody>
        <ModalFooter gap={2}>
          <Button size="sm" variant="ghost" onClick={onClose} isDisabled={submitting}>
            Cancel
          </Button>
          <SolidActionButton
            size="sm"
            onClick={handleSubmit}
            isDisabled={!text.trim() || submitting}
            isLoading={submitting}>
            Submit Statement
          </SolidActionButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

const EncounterStatementRow = ({
  encounter,
  patientId,
  assessmentId,
  practiceId,
}: {
  encounter: WithId<Encounter>
  patientId: string
  assessmentId: string
  practiceId: string
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure()

  const statementsRef = useMemo(
    () =>
      query(
        collection(
          db,
          getPatientStatementsCollectionPath(patientId),
        ) as CollectionReference<PatientStatement>,
        where('encounterId', '==', encounter.id),
      ),
    [encounter.id, patientId],
  )
  const { dataArr: statements } = useQuery<PatientStatement>(statementsRef)
  const hasStatement = (statements ?? []).length > 0

  return (
    <>
      <Flex
        align="center"
        justify="space-between"
        py={2}
        px={1}
        borderBottom="1px solid"
        borderColor="gray.100"
        gap={3}
        flexWrap="wrap">
        <Box>
          <Text fontSize="sm" fontWeight={500}>
            {encounter.date ?? '—'}
          </Text>
          {hasStatement ? (
            <Text fontSize="xs" color="orange.600" mt={0.5}>
              Statement submitted
            </Text>
          ) : null}
        </Box>
        <Button size="xs" colorScheme="orange" variant="outline" onClick={onOpen}>
          {hasStatement ? 'View / Add Statement' : 'Add a Written Statement'}
        </Button>
      </Flex>
      {isOpen ? (
        <StatementModal
          isOpen={isOpen}
          onClose={onClose}
          encounter={encounter}
          patientId={patientId}
          assessmentId={assessmentId}
          practiceId={practiceId}
        />
      ) : null}
    </>
  )
}

export const PatientStatementsSection = ({ assessmentId, patientId, practiceId }: Props) => {
  const [expanded, setExpanded] = useState(false)

  const encountersRef = useMemo(
    () =>
      query(
        collection(db, ENCOUNTERS) as CollectionReference<Encounter>,
        where('assessmentId', '==', assessmentId),
        where('status', '==', EncounterStatus.AUTHENTICATED),
      ),
    [assessmentId],
  )
  const { dataArr: encounters, loading } = useQuery<Encounter>(encountersRef)

  const authenticatedEncounters = useMemo(() => encounters ?? [], [encounters])

  return (
    <Box mt={6} border="1px solid" borderColor="orange.200" borderRadius={8} overflow="hidden">
      <Flex
        align="center"
        justify="space-between"
        px={4}
        py={3}
        bg="orange.50"
        cursor="pointer"
        onClick={() => setExpanded(v => !v)}>
        <Text fontWeight={600} fontSize="sm" color="orange.800">
          My Medical Records — Add a Statement
        </Text>
        <Text fontSize="xs" color="orange.500">
          {expanded ? 'Collapse' : 'Expand'}
        </Text>
      </Flex>
      <Collapse in={expanded}>
        <Box px={4} py={3}>
          <Text fontSize="xs" color="gray.600" mb={4}>
            Under New York State law (10 NYCRR 405.10), you have the right to add a written
            statement to your medical record if you disagree with any entry. Statements become a
            permanent part of your record and cannot be removed.
          </Text>
          {loading ? (
            <Flex justify="center" py={4}>
              <Spinner size="sm" />
            </Flex>
          ) : authenticatedEncounters.length === 0 ? (
            <Text fontSize="sm" color="gray.400" fontStyle="italic">
              No authenticated visit notes on file yet.
            </Text>
          ) : (
            <VStack spacing={0} align="stretch" divider={<Divider />}>
              {authenticatedEncounters.map(enc => (
                <EncounterStatementRow
                  key={enc.id}
                  encounter={enc}
                  patientId={patientId}
                  assessmentId={assessmentId}
                  practiceId={practiceId}
                />
              ))}
            </VStack>
          )}
        </Box>
      </Collapse>
    </Box>
  )
}
